import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Building2, User, Bell, QrCode, BarChart3, ArrowRight, CheckCircle, MessageCircle, Shield, Zap, Clock, TrendingUp, Heart, Target, Users, Mail } from 'lucide-react';

import Navbar from '../components/Navbar';

const SplashScreen = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* ===== NAVBAR ===== */}
       {/* ============================================================
           PASSO 2 — Substitua qualquer <nav> antiga por este componente
           Basta colocar <Navbar /> no topo de cada página.
           O link ativo é detectado automaticamente pela URL.
          ============================================================ */}
      <Navbar />

      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden bg-gradient-to-br from-white via-red-50/30 to-white">
        <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl -z-10" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-primary/5 rounded-full blur-3xl -z-10" />
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <span className="inline-block bg-primary/10 text-primary text-sm font-semibold px-4 py-1.5 rounded-full mb-6">
                ✨ Nunca mais perca um cliente
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight mb-6">
                Lembretes automáticos para{' '}
                <span className="text-primary">seus clientes</span>
              </h1>
              <p className="text-lg text-gray-500 mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0">
                Automatize lembretes de renovação e retorno via <strong className="text-gray-700">WhatsApp e email</strong>. 
                Cadastro rápido por QR Code e gestão completa em um só lugar.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button
                  data-testid="b2b-card-button"
                  onClick={() => navigate('/auth?mode=business')}
                  className="rounded-full bg-primary hover:bg-primary-hover text-white px-8 py-6 text-base font-semibold shadow-lg shadow-primary/25 hover:shadow-xl transition-all"
                >
                  <Building2 className="w-5 h-5 mr-2" />
                  Quero Avisar
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  data-testid="b2c-card-button"
                  onClick={() => navigate('/timeline')}
                  variant="outline"
                  className="rounded-full border-2 border-gray-200 hover:border-primary px-8 py-6 text-base font-semibold text-gray-700 hover:text-primary transition-all"
                >
                  <User className="w-5 h-5 mr-2" />
                  Quero Ser Avisado
                </Button>
              </div>
            </div>

            <div className="relative">
              <img src="/In_sync-lembrou.svg" alt="Sincronização" className="w-full h-auto max-w-lg mx-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* ===== SOCIAL PROOF BAR ===== */}
      <section className="bg-gray-900 py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-3xl sm:text-4xl font-bold text-primary">+148</p>
              <p className="text-sm text-gray-400 mt-1">Empresas Cadastradas</p>
            </div>
            <div>
              <p className="text-3xl sm:text-4xl font-bold text-white">+4.950</p>
              <p className="text-sm text-gray-400 mt-1">Lembretes Enviados</p>
            </div>
            <div>
              <p className="text-3xl sm:text-4xl font-bold text-primary">98%</p>
              <p className="text-sm text-gray-400 mt-1">Satisfação</p>
            </div>
            <div>
              <p className="text-3xl sm:text-4xl font-bold text-white">+40%</p>
              <p className="text-sm text-gray-400 mt-1">Taxa de Retorno</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== PROBLEMA/SOLUÇÃO ===== */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-primary/10 text-primary text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
              O Desafio
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Por que seus clientes não retornam?
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Três problemas comuns que afetam profissionais todos os dias
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-all">
              <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mb-6">
                <Clock className="w-7 h-7 text-red-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Esquecem a data de retorno
              </h3>
              <p className="text-gray-500 leading-relaxed">
                Seu cliente sai satisfeito mas esquece quando deve voltar para a próxima consulta, revisão ou renovação.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-all">
              <div className="w-14 h-14 rounded-2xl bg-orange-50 flex items-center justify-center mb-6">
                <Users className="w-7 h-7 text-orange-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Você não tem tempo de ligar
              </h3>
              <p className="text-gray-500 leading-relaxed">
                Sua agenda está lotada. Ficar ligando para lembrar cada cliente é inviável e toma tempo precioso.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-all">
              <div className="w-14 h-14 rounded-2xl bg-yellow-50 flex items-center justify-center mb-6">
                <MessageCircle className="w-7 h-7 text-yellow-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Comunicação manual é falha
              </h3>
              <p className="text-gray-500 leading-relaxed">
                Anotações perdidas, lembretes que você mesmo esquece de enviar, oportunidades de atendimento perdidas.
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <div className="inline-flex items-center gap-3 bg-primary/10 text-primary px-6 py-4 rounded-2xl font-semibold">
              <Zap className="w-5 h-5" />
              O Lembrou resolve isso com lembretes automáticos que chegam na hora certa
            </div>
          </div>
        </div>
      </section>

      {/* ===== COMO FUNCIONA ===== */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-primary/10 text-primary text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
              Como Funciona
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Simples como 1, 2, 3
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Configure uma vez e deixe o sistema trabalhar por você
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {/* Passo 1 */}
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-12 h-12 rounded-full bg-primary text-white font-bold text-xl flex items-center justify-center shadow-lg">
                1
              </div>
              <div className="bg-gradient-to-br from-primary/5 to-transparent rounded-2xl p-8 pt-12 border border-primary/10">
                <QrCode className="w-12 h-12 text-primary mb-6" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">Cadastro Rápido</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Seu cliente escaneia seu QR Code pessoal</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Preenche nome, telefone e data do próximo retorno</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Pronto! Cadastrado em menos de 30 segundos</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Passo 2 */}
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-12 h-12 rounded-full bg-primary text-white font-bold text-xl flex items-center justify-center shadow-lg">
                2
              </div>
              <div className="bg-gradient-to-br from-primary/5 to-transparent rounded-2xl p-8 pt-12 border border-primary/10">
                <Bell className="w-12 h-12 text-primary mb-6" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">Lembretes Automáticos</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Sistema envia lembretes via WhatsApp ou email</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>30, 15, 7 e 1 dia antes da data programada</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Mensagem personalizada com seu nome e contato</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Passo 3 */}
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-12 h-12 rounded-full bg-primary text-white font-bold text-xl flex items-center justify-center shadow-lg">
                3
              </div>
              <div className="bg-gradient-to-br from-primary/5 to-transparent rounded-2xl p-8 pt-12 border border-primary/10">
                <TrendingUp className="w-12 h-12 text-primary mb-6" />
                <h3 className="text-xl font-bold text-gray-900 mb-3">Cliente Retorna</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Seu cliente lembra do compromisso</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Mais retornos = mais fidelização</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Aumente seu faturamento sem esforço extra</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== PARA QUEM É ===== */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-primary/10 text-primary text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
              Para Quem é
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Ideal para profissionais que valorizam o relacionamento com clientes
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Seja qual for sua área de atuação, se você precisa manter contato periódico com seus clientes, o Lembrou é para você
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[
              { emoji: '🏥', title: 'Veterinários', desc: 'Vacinas, check-ups, retornos' },
              { emoji: '💇', title: 'Salões de Beleza', desc: 'Cortes, colorações, depilação' },
              { emoji: '🚗', title: 'Mecânicos', desc: 'Revisões, troca de óleo' },
              { emoji: '💪', title: 'Personal Trainers', desc: 'Avaliações, treinos' },
              { emoji: '🦷', title: 'Dentistas', desc: 'Limpezas, tratamentos' },
              { emoji: '🏢', title: 'Corretores', desc: 'Renovações de seguros' },
              { emoji: '💅', title: 'Manicures', desc: 'Manutenções regulares' },
              { emoji: '📋', title: 'E muito mais...', desc: 'Qualquer serviço periódico' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-all border border-gray-100">
                <div className="text-4xl mb-3">{item.emoji}</div>
                <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PARA PROFISSIONAIS ===== */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <img src="/Upload-lembrou.svg" alt="Para Profissionais" className="w-full h-auto max-w-md mx-auto" />
            </div>
            
            <div className="order-1 lg:order-2">
              <span className="inline-block bg-primary/10 text-primary text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
                Para Profissionais Autônomos
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Facilite o dia a dia com automação inteligente
              </h2>
              <p className="text-gray-500 mb-8 leading-relaxed">
                Cadastre seus clientes, defina os prazos e deixe o Lembrou cuidar do resto. 
                Lembretes automáticos por email e WhatsApp garantem que nenhuma renovação seja esquecida.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  { icon: QrCode, title: 'QR Code Personalizado', desc: 'Cada profissional tem seu QR Code exclusivo para cadastro rápido de clientes.' },
                  { icon: Bell, title: 'Lembretes Automáticos', desc: 'Notificações 30, 15, 7 e 1 dia antes do vencimento via WhatsApp e email.' },
                  { icon: BarChart3, title: 'Dashboard Completo', desc: 'Acompanhe clientes, vencimentos e histórico em um só lugar.' },
                ].map((f, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <f.icon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{f.title}</h3>
                      <p className="text-sm text-gray-500">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Button
                onClick={() => navigate('/auth?mode=business')}
                className="rounded-full bg-primary hover:bg-primary-hover text-white px-8 py-5 font-semibold shadow-md"
              >
                Comece Grátis
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ===== BENEFÍCIOS ===== */}
      <section className="py-20 bg-gradient-to-br from-primary/5 to-transparent">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-primary/10 text-primary text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
              Benefícios
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Resultados reais para seu negócio
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Veja o impacto positivo na sua rotina e no seu faturamento
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <div className="w-14 h-14 rounded-2xl bg-green-50 flex items-center justify-center mb-6">
                <TrendingUp className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">+40% de retorno</h3>
              <p className="text-gray-500">
                Clientes lembrados voltam. Aumente sua taxa de retorno significativamente.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mb-6">
                <Clock className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Economize 10h/semana</h3>
              <p className="text-gray-500">
                Pare de fazer lembretes manuais. Deixe o sistema trabalhar por você.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <div className="w-14 h-14 rounded-2xl bg-purple-50 flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-purple-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Mais receita recorrente</h3>
              <p className="text-gray-500">
                Mais retornos = mais atendimentos = mais faturamento constante.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <div className="w-14 h-14 rounded-2xl bg-pink-50 flex items-center justify-center mb-6">
                <Heart className="w-7 h-7 text-pink-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Clientes satisfeitos</h3>
              <p className="text-gray-500">
                Seu cliente se sente cuidado quando você lembra dele na hora certa.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <div className="w-14 h-14 rounded-2xl bg-indigo-50 flex items-center justify-center mb-6">
                <Shield className="w-7 h-7 text-indigo-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Profissionalismo</h3>
              <p className="text-gray-500">
                Demonstre organização e cuidado com lembretes pontuais e personalizados.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <div className="w-14 h-14 rounded-2xl bg-orange-50 flex items-center justify-center mb-6">
                <Zap className="w-7 h-7 text-orange-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Fácil de usar</h3>
              <p className="text-gray-500">
                Configure em 5 minutos. Sem necessidade de treinamento ou conhecimento técnico.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== PARA USUÁRIOS ===== */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block bg-primary/10 text-primary text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
                Para Usuários
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Receba lembretes dos seus profissionais favoritos
              </h2>
              <p className="text-gray-500 mb-8 leading-relaxed">
                Escaneia o QR Code do seu profissional e receba lembretes automáticos quando for hora de voltar. 
                Nunca mais esqueça uma consulta, revisão ou renovação.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  { icon: QrCode, title: 'Cadastro em 30 segundos', desc: 'Escaneie o QR Code e pronto. Nada para instalar.' },
                  { icon: Bell, title: 'Lembretes no WhatsApp', desc: 'Receba notificações na hora certa, sem esquecer.' },
                  { icon: MessageCircle, title: 'Totalmente gratuito', desc: 'Você não paga nada. Só recebe e aproveita.' },
                ].map((f, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <f.icon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{f.title}</h3>
                      <p className="text-sm text-gray-500">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Button
                onClick={() => navigate('/timeline')}
                className="rounded-full bg-primary hover:bg-primary-hover text-white px-8 py-5 font-semibold shadow-md"
              >
                Ver Meus Lembretes
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            <div>
              <img src="/Uploading-lembrou.svg" alt="Para Usuários" className="w-full h-auto max-w-md mx-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-primary/10 text-primary text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
              FAQ
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Dúvidas Frequentes
            </h2>
            <p className="text-gray-500">
              Tudo que você precisa saber sobre o Lembrou
            </p>
          </div>

          <div className="space-y-6">
            {[
              {
                q: 'Meu cliente precisa instalar algum aplicativo?',
                a: 'Não! O cadastro é feito diretamente pelo navegador ao escanear o QR Code. Nada para baixar ou instalar.'
              },
              {
                q: 'Como funcionam os lembretes?',
                a: 'Você define a data de retorno no cadastro. O sistema envia automaticamente os lembretes via WhatsApp ou email nos intervalos programados (30, 15, 7 e 1 dia antes).'
              },
              {
                q: 'Posso personalizar as mensagens?',
                a: 'Sim! As mensagens saem com seu nome e informações de contato personalizadas, mantendo sua identidade profissional.'
              },
              {
                q: 'E se meu cliente não tem WhatsApp?',
                a: 'Os lembretes podem ser enviados por email também. O sistema usa o canal de preferência do cliente.'
              },
              {
                q: 'Posso usar para mais de um tipo de serviço?',
                a: 'Sim! Você pode cadastrar diferentes tipos de retorno e gerenciar tudo no mesmo dashboard.'
              },
              {
                q: 'Os dados dos meus clientes são seguros?',
                a: 'Sim! Utilizamos criptografia e seguimos a LGPD para proteção de dados. Seus clientes estão seguros.'
              },
              {
                q: 'Funciona para quantos clientes?',
                a: 'O plano Essencial permite até 20 contratos ativos. O plano Profissional não tem limite de clientes.'
              },
              {
                q: 'Quanto custa?',
                a: 'Você pode começar gratuitamente com o plano Essencial (até 20 contratos). O plano Profissional com lembretes ilimitados custa R$ 19,99/mês.'
              },
            ].map((faq, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="font-bold text-gray-900 mb-3 text-lg">{faq.q}</h3>
                <p className="text-gray-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PLANOS E PREÇOS ===== */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block bg-primary/10 text-primary text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
              Planos e Preços
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Transparência total
            </h2>
            <p className="text-gray-500">Escolha o plano que faz sentido para você</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {/* Essencial */}
            <div className="bg-white rounded-2xl border-2 border-gray-100 p-8 hover:border-primary/30 hover:shadow-lg transition-all">
              <p className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-2">Essencial</p>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl font-extrabold text-gray-900">R$ 0</span>
                <span className="text-sm text-gray-400">/mês</span>
              </div>
              <ul className="space-y-3 mb-8">
                {['Até 20 contratos ativos', 'Lembretes por email', 'Lembretes por WhatsApp', 'Painel de controle', 'QR Code por cliente'].map((t, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                    <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />{t}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => navigate('/auth?mode=business')}
                className="w-full rounded-xl bg-primary hover:bg-primary-hover text-white py-5 font-semibold"
              >
                Começar Grátis
              </Button>
            </div>

            {/* Profissional */}
            <div className="bg-gray-900 rounded-2xl p-8 text-white relative shadow-xl scale-105">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white text-xs font-bold px-4 py-1 rounded-full">
                Popular
              </div>
              <p className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-2">Profissional</p>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl font-extrabold text-white">R$ 19</span>
                <span className="text-sm text-gray-400">,99/mês</span>
              </div>
              <ul className="space-y-3 mb-8">
                {['Contratos ilimitados', 'Lembretes por email', 'Lembretes por WhatsApp', 'Envio manual de WhatsApp', 'Painel de controle'].map((t, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-300">
                    <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />{t}
                  </li>
                ))}
              </ul>
              <Button 
                onClick={() => navigate('/auth?mode=business&upgrade=pro')}
                className="w-full rounded-xl bg-primary hover:bg-primary-hover text-white py-5 font-semibold"
              >
                Assinar Agora
              </Button>
            </div>

            {/* Premium */}
            <div className="bg-white rounded-2xl border-2 border-gray-100 p-8 hover:border-primary/30 hover:shadow-lg transition-all">
              <p className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-2">Premium</p>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl font-extrabold text-gray-900">R$ 59</span>
                <span className="text-sm text-gray-400">,99/mês</span>
              </div>
              <ul className="space-y-3 mb-8">
                {['Tudo do Profissional', 'Marca personalizada', 'Painel de histórico', 'Suporte prioritário'].map((t, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                    <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />{t}
                  </li>
                ))}
              </ul>
              <Button className="w-full rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-500 py-5 font-semibold" disabled>
                Em Breve
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA FINAL ===== */}
      <section className="py-20 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Comece a recuperar clientes hoje
          </h2>
          <p className="text-white/90 mb-8 text-lg max-w-2xl mx-auto">
            Experimente grátis e veja seus clientes retornando com mais frequência. 
            Configure em 5 minutos, sem necessidade de treinamento.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button
              onClick={() => navigate('/auth?mode=business')}
              className="rounded-full bg-white text-primary hover:bg-gray-100 px-8 py-6 text-base font-semibold shadow-lg"
            >
              Criar Conta Grátis
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              onClick={() => navigate('/auth?mode=business')}
              variant="outline"
              className="rounded-full border-2 border-white/30 text-white hover:bg-white/10 px-8 py-6 text-base font-semibold"
            >
              Fazer Login
            </Button>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-6 text-white/80 text-sm">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4" />
              Configuração em 5 minutos
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Sem necessidade de treinamento
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Sem compromisso
            </div>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-10">
            <div>
              <img src="/lembrou-logo-png.png" alt="Lembrou?" className="h-8 mb-4 brightness-0 invert" />
              <p className="text-sm text-gray-400">
                Sistema de lembretes automáticos para profissionais.
              </p>
              <p className="text-xs text-gray-500 mt-4">
                2026 © Lembrou.com.br
                <br />Todos os direitos reservados.
              </p>
            </div>
            <div>
              <p className="font-semibold mb-4 text-sm">Para Profissionais</p>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><button onClick={() => navigate('/auth?mode=business')} className="hover:text-white transition-colors">Cadastre-se Grátis</button></li>
                <li><button onClick={() => navigate('/auth?mode=business')} className="hover:text-white transition-colors">Login</button></li>
                <li><button onClick={() => navigate('/planos')} className="hover:text-white transition-colors">Planos e Preços</button></li>
              </ul>
            </div>
            <div>
              <p className="font-semibold mb-4 text-sm">Recursos</p>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><button onClick={() => navigate('/como-funciona')} className="hover:text-white transition-colors">Como Funciona</button></li>
                <li><button onClick={() => navigate('/faq')} className="hover:text-white transition-colors">Perguntas Frequentes</button></li>
                <li><button onClick={() => navigate('/sobre')} className="hover:text-white transition-colors">Sobre o Lembrou</button></li>
              </ul>
            </div>
            <div>
              <p className="font-semibold mb-4 text-sm">Para Usuários</p>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><button onClick={() => navigate('/timeline')} className="hover:text-white transition-colors">Meus Lembretes</button></li>
                <li><button onClick={() => navigate('/auth?mode=user')} className="hover:text-white transition-colors">Criar Conta</button></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <span className="text-xs text-gray-600">© 2026 Lembrou — Lembretes automáticos para profissionais</span>
            <div className="flex items-center gap-6 text-xs text-gray-500">
              <button onClick={() => navigate('/termos')} className="hover:text-white transition-colors">Termos de Uso</button>
              <button onClick={() => navigate('/privacidade')} className="hover:text-white transition-colors">Política de Privacidade</button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SplashScreen;